import { match, P } from 'ts-pattern'

/**
 * Source: https://stackoverflow.com/a/14919494
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
export function humanFileSize(bytes: number, si: boolean = false, dp: number = 1) {
  const threshold = si ? 1000 : 1024

  if (Math.abs(bytes) < threshold) {
    return bytes + ' B'
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
  let u = -1
  const r = 10 ** dp

  do {
    bytes /= threshold
    u = u + 1
  } while (Math.round(Math.abs(bytes) * r) / r >= threshold && u < units.length - 1)

  return bytes.toFixed(dp) + ' ' + units[u]
}

/**
 * Attempts to find the MIME-type from an ArrayBuffer
 * Source: https://stackoverflow.com/a/75439718
 * @param arrayBuffer
 */
export function getMimeType(arrayBuffer: Buffer) {
  const uint8arr = new Uint8Array(arrayBuffer)

  const len = 12
  if (uint8arr.length >= len) {
    const signatureArr = Array.from(uint8arr.slice(0, len))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()

    const primarySignature = signatureArr.slice(0, 8)

    return match(primarySignature)
      .with('89504E47', () => 'image/png')
      .with('47494638', () => 'image/gif')
      .with('FF494433', () => 'audio/mp3')
      .with('25504446', () => 'application/pdf')
      .with('424D0000', () => 'image/bmp')
      .with('49492A00', '4D4D002A', () => 'image/tiff')
      .with('504B0304', () => 'application/zip')
      .with(P.string.startsWith('FFD8FF'), () => 'image/jpeg')
      .with('52494646', () => {
        const webpSignatureArr = Array.from(uint8arr.slice(8, 12))
          .map((byte) => byte.toString(16).padStart(2, '0'))
          .join('')
          .toUpperCase()

        if (webpSignatureArr.startsWith('57454250')) {
          return 'image/webp'
        }
        return null
      })
      .otherwise(() => null)
  }
  return null
}
